/**
 * <TutorTrainingVideo> — la vidéo de formation du masteur, dans la page (molecule).
 *
 * L'ancien `TrainingVideoPlayer` : la vidéo se joue dans l'app, à la largeur de la colonne, au
 * format 16:9, sur un fond de nuit. Quand le serveur ne donne pas d'adresse à embarquer, la vidéo
 * s'ouvre dans le navigateur : c'est alors un bouton, « Voir la vidéo ».
 *
 * Le lecteur lui-même (l'iframe du web, la WebView du téléphone) n'est pas ici : le design system
 * n'embarque pas encore de page web hors de `<LinkDisplay>` et `<RichText>`. Il arrive par `player`
 * ; sans lui, le cadre montre l'affiche et son bouton de lecture (`onPlay`).
 */
import React from "react"
import { Pressable, View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { Button } from "../../atoms/Buttons/Button"
import { Stack } from "../../atoms/containers/Stack"
import { IconBubble } from "../../atoms/icons/IconBubble"
import { Text } from "../../atoms/typography/Text"

export type TutorTrainingVideoProps = {
    /** Le serveur donne une adresse à embarquer : la vidéo se joue dans la page. */
    embedded: boolean
    /** Le lecteur, posé par l'écran. Absent : l'affiche. */
    player?: React.ReactNode
    /** L'affiche touchée, ou « Voir la vidéo » quand elle s'ouvre dans le navigateur. */
    onPlay: () => void
    testID?: string
}

const TEXTS = { open: "Voir la vidéo", play: "Lire la vidéo de formation" }

export function TutorTrainingVideo(props: TutorTrainingVideoProps) {
    const { embedded, player, onPlay, testID } = props

    if (!embedded) {
        return <Button label={TEXTS.open} iconName="play" fitContent onPress={onPlay} testID={testID} />
    }

    return (
        <View style={styles.frame} testID={testID}>
            {player ?? (
                <Pressable
                    onPress={onPlay}
                    accessibilityRole="button"
                    accessibilityLabel={TEXTS.play}
                    style={({ pressed }) => [styles.poster, pressed && styles.pressed]}
                >
                    <Stack gap="2" align="center">
                        <IconBubble iconName="play" size={56} color="role" iconColor="text-on-role" />
                        <Text size="text-sm" weight="semibold" color="text-on-night">
                            {TEXTS.play}
                        </Text>
                    </Stack>
                </Pressable>
            )}
        </View>
    )
}

const styles = StyleSheet.create((theme) => ({
    frame: {
        width: "100%",
        aspectRatio: 16 / 9,
        borderRadius: theme.radius.md,
        overflow: "hidden",
        backgroundColor: theme.colors["surface-night"],
    },
    poster: { flex: 1, alignItems: "center", justifyContent: "center" },
    pressed: { opacity: 0.8 },
}))
